import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const emptyPart = {
  name: "",
  sku: "",
  quantity: 0,
  min_stock: 0,
  unit_cost: 0,
  supplier: "",
  category: "",
  notes: "",
  type: "part",
};

function InlineNumberCell({ value, onSave, testid, className, min = 0 }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(String(value ?? 0));
  }, [value, editing]);

  const commit = async () => {
    const next = Number(draft);
    if (Number.isNaN(next) || next < min) {
      setDraft(String(value ?? 0));
      setEditing(false);
      return;
    }
    if (next === Number(value ?? 0)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        min={min}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { setDraft(String(value ?? 0)); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
        className={`w-20 px-2 py-0.5 border border-[#002FA7] rounded-sm font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${className || ""}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      data-testid={testid}
      title="Click to edit"
      className={`font-mono text-sm px-2 py-0.5 rounded-sm hover:bg-[#002FA7]/10 hover:ring-1 hover:ring-[#002FA7]/40 ${className || ""}`}
    >
      {value ?? 0}
    </button>
  );
}

function InventoryTable({ title, testid, items, query, onEdit, onDelete, onQuickUpdate }) {
  const filtered = items.filter((p) => {
    const q = query.toLowerCase();
    return (
      !q ||
      p.name.toLowerCase().includes(q) ||
      p.sku.toLowerCase().includes(q) ||
      (p.supplier || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q)
    );
  });
  const lowCount = filtered.filter((p) => p.low_stock).length;

  return (
    <section className="mt-8" data-testid={testid}>
      <div className="flex items-end justify-between border-b border-zinc-200 pb-3">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-zinc-950">{title}</h2>
          <div className="text-xs text-zinc-500 font-mono mt-1">
            {filtered.length} items{lowCount > 0 && <span className="text-[#FF2400] ml-2">· {lowCount} low stock</span>}
          </div>
        </div>
      </div>
      <div className="mt-4 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Status", "Name", "SKU", "Qty", "Min", "Unit Cost", "Supplier", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-zinc-500">
                  None yet. Click "Add" to create one.
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr
                key={p.id}
                className="hover:bg-zinc-50 cursor-pointer"
                data-testid={`part-row-${p.sku}`}
                onClick={() => onEdit(p)}
                title="Click to edit"
              >
                <td className="px-4 py-3">
                  {p.low_stock ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold bg-red-50 text-red-600 border border-red-200 rounded-sm">
                      <AlertTriangle className="w-3 h-3" /> Low
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-sm">
                      In stock
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-zinc-950">{p.name}</td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{p.sku}</td>
                <td className={`px-4 py-3 text-sm font-mono font-bold ${p.low_stock ? "text-[#FF2400]" : "text-zinc-900"}`} onClick={(e) => e.stopPropagation()}>
                  <InlineNumberCell
                    value={p.quantity}
                    onSave={(next) => onQuickUpdate(p, { quantity: next })}
                    testid={`part-qty-${p.sku}`}
                    className={p.low_stock ? "text-[#FF2400] font-bold" : "text-zinc-900 font-bold"}
                  />
                </td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-700" onClick={(e) => e.stopPropagation()}>
                  <InlineNumberCell
                    value={p.min_stock}
                    onSave={(next) => onQuickUpdate(p, { min_stock: next })}
                    testid={`part-min-${p.sku}`}
                    className="text-zinc-700"
                  />
                </td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-900">{currency(p.unit_cost)}</td>
                <td className="px-4 py-3 text-sm text-zinc-700">{p.supplier || "—"}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onEdit(p)}
                    data-testid={`edit-part-${p.sku}`}
                    className="text-zinc-500 hover:text-zinc-950 mr-2"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(p)}
                    data-testid={`delete-part-${p.sku}`}
                    className="text-zinc-500 hover:text-[#FF2400]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Parts() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPart);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/parts"), api.get("/suppliers")]);
      setItems(p.data);
      setSuppliers(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = (type = "part") => {
    setEditing(null);
    setForm({ ...emptyPart, type });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyPart, ...p });
    setDialogOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        min_stock: Number(form.min_stock),
        unit_cost: Number(form.unit_cost),
      };
      if (editing) {
        await api.patch(`/parts/${editing.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post("/parts", payload);
        toast.success("Added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const parts = items.filter((p) => (p.type || "part") === "part");
  const supplies = items.filter((p) => p.type === "supply");
  const turboTiming = items.filter((p) => p.type === "turbo_timing");
  const naTiming = items.filter((p) => p.type === "na_timing");

  const onQuickUpdate = async (part, patch) => {
    // Optimistic in-place update so the row updates instantly
    setItems((prev) => prev.map((it) => (it.id === part.id ? { ...it, ...patch } : it)));
    try {
      await api.patch(`/parts/${part.id}`, patch);
      // Recompute low_stock server-side by pulling the fresh row silently
      const { data } = await api.get(`/parts/${part.id}`).catch(() => ({ data: null }));
      if (data) setItems((prev) => prev.map((it) => (it.id === part.id ? data : it)));
      toast.success(`${part.name} updated`);
    } catch (err) {
      // Revert on failure
      setItems((prev) => prev.map((it) => (it.id === part.id ? part : it)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <PageHeader
        eyebrow="Stock"
        title="Inventory"
        description="Track every part and supply. Low-stock items are flagged for reorder automatically."
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => openCreate("part")}
                data-testid="add-part-btn"
                className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">
                  {editing ? "Edit item" : "New item"}
                </DialogTitle>
                <DialogDescription>
                  Choose whether this item is a Part or a Supply. Both appear on the Inventory page.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={onSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="part-form">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="part-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="part">Part</SelectItem>
                      <SelectItem value="supply">Supply</SelectItem>
                      <SelectItem value="turbo_timing">Turbo Timing Job</SelectItem>
                      <SelectItem value="na_timing">NA Timing Job</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Category</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-sm border-zinc-300" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="part-name-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">SKU</Label>
                  <Input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="part-sku-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Quantity</Label>
                  <Input required type="number" min={0} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="part-qty-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Min Stock</Label>
                  <Input required type="number" min={0} value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="part-min-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Unit Cost</Label>
                  <Input required type="number" min={0} step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} data-testid="part-cost-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Supplier</Label>
                  <Select
                    value={form.supplier || "__none__"}
                    onValueChange={(v) => setForm({ ...form, supplier: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="part-supplier-select">
                      <SelectValue placeholder="Select supplier..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {suppliers.length === 0 && (
                    <div className="text-xs text-zinc-500 mt-1">
                      No suppliers yet.{" "}
                      <Link to="/suppliers" className="font-semibold text-[#002FA7] hover:underline">
                        Add one
                      </Link>
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-zinc-300" rows={2} />
                </div>
                <DialogFooter className="col-span-2 mt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="part-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Saving..." : editing ? "Save changes" : "Add"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, SKU, supplier..."
            data-testid="parts-search"
            className="pl-9 rounded-sm border-zinc-300"
          />
        </div>
        <div className="text-xs text-zinc-500 font-mono">
          {loading ? "Loading..." : `${parts.length} parts · ${supplies.length} supplies`}
        </div>
      </div>

      <InventoryTable
        title="Parts"
        testid="parts-table"
        items={parts}
        query={query}
        onEdit={openEdit}
        onDelete={onDelete}
        onQuickUpdate={onQuickUpdate}
      />
      <InventoryTable
        title="Supplies"
        testid="supplies-table"
        items={supplies}
        query={query}
        onEdit={openEdit}
        onDelete={onDelete}
        onQuickUpdate={onQuickUpdate}
      />
    </div>
  );
}
